import { create } from "zustand";

/**
 * One anchored instruction in a guided onboarding chain: the CSS selector of
 * the control the pointer card attaches to, plus the i18n key (inside the
 * "onboarding" namespace) of the one-line instruction shown in the card.
 */
export interface PointerStep {
  selector: string;
  textKey: string;
}

/**
 * Cross-tree store for the Stripe-style onboarding pointer card. Armed by the
 * step detail overlay right before it navigates ("Fortfahren"); consumed by the
 * App-level <OnboardingPointer /> host, which survives the route change and
 * waits for the target to mount. Modeled as a step ARRAY so multi-step chains
 * (click A, then B inside the opened modal) stay possible — clicking a target
 * advances to the next step; the chain ends after the last one.
 */
type PointerState = {
  /** Onboarding step key the chain belongs to (telemetry/debug), null = idle. */
  stepKey: string | null;
  steps: PointerStep[];
  index: number;
  startPointer: (stepKey: string, steps: PointerStep[]) => void;
  advancePointer: () => void;
  stopPointer: () => void;
};

export const usePointerStore = create<PointerState>((set) => ({
  stepKey: null,
  steps: [],
  index: 0,
  startPointer: (stepKey, steps) =>
    set(steps.length > 0 ? { stepKey, steps, index: 0 } : { stepKey: null, steps: [], index: 0 }),
  advancePointer: () =>
    set((s) =>
      s.index + 1 < s.steps.length
        ? { index: s.index + 1 }
        : { stepKey: null, steps: [], index: 0 },
    ),
  stopPointer: () => set({ stepKey: null, steps: [], index: 0 }),
}));

/** Arm the pointer chain for an onboarding step (imperative, non-React callers). */
export function startPointer(stepKey: string, steps: PointerStep[]): void {
  usePointerStore.getState().startPointer(stepKey, steps);
}

/** End the active pointer chain, if any. */
export function stopPointer(): void {
  usePointerStore.getState().stopPointer();
}
